import { useEffect, useLayoutEffect, useRef } from "react";

import { ScrollAreaEnum } from "../enums";
import { RydeUiUnit } from "../models";
import {
  forgetScrollPosition,
  getRememberedScrollPosition,
  rememberScrollPosition,
  SCROLL_POSITION_RESTORE_TIMEOUT_MS,
  SCROLL_POSITION_USER_INTENT_EVENTS,
} from "./parts";

/**
 * Keeps a scrollable area where the user left it across a refresh of the page it is on.
 *
 * `isRefreshing` is what makes it a refresh rather than a navigation: while it is true the area
 * stops recording (a refresh that empties the rows clamps the offset to 0, and recording that would
 * throw the position away), and every time it turns false the remembered offset is applied again.
 * Callers pass the app-wide `apiDataFetchingAtom` deliberately: the ticket asks for a restore after
 * "an action that invokes a refresh", which is the same set of calls that atom already covers, and
 * there is no per-grid fetching flag to narrow it to. Nothing blocks the container while that is
 * true, so a scroll the user makes mid-fetch is treated as intent and drops the remembered offset
 * — otherwise the restore would undo a move they made on purpose.
 *
 * `element` is null until the container mounts — and for a display that renders more than one
 * container, for the ones whose offset is not being remembered. The hook does nothing until it
 * arrives.
 *
 * `onApplied` runs after every write, for a container whose siblings have to be dragged along (a
 * frozen row and column, say). It cannot be left to the element's own scroll listener: re-assigning
 * the offset it already holds changes nothing and therefore fires no scroll event, so only the first
 * frame would notify anyone. It must be a stable reference, or the restore re-runs on it.
 */
export const useScrollPositionMemory = ({
  element,
  area,
  pageKey,
  isRefreshing,
  intentElement,
  onApplied,
}: {
  element: HTMLElement | null;
  area: ScrollAreaEnum;
  pageKey: RydeUiUnit;
  isRefreshing: boolean;
  intentElement?: HTMLElement | null;
  onApplied?: () => void;
}): void => {
  const isRestoringRef = useRef(false);

  useEffect(() => {
    if (!element || isRefreshing) {
      return undefined;
    }

    let frame: number | undefined;

    const handleScroll = (): void => {
      // A restore assigns scrollTop, which fires this listener. Recording the intermediate value
      // the half-rendered content can currently satisfy would overwrite the target with a clamp.
      if (isRestoringRef.current || frame !== undefined) {
        return;
      }

      frame = requestAnimationFrame(() => {
        frame = undefined;
        rememberScrollPosition({
          area,
          pageKey,
          scrollTop: element.scrollTop,
          scrollLeft: element.scrollLeft,
        });
      });
    };

    element.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      if (frame !== undefined) {
        cancelAnimationFrame(frame);
      }

      element.removeEventListener("scroll", handleScroll);
    };
  }, [area, element, isRefreshing, pageKey]);

  useEffect(() => {
    const intentTarget = intentElement ?? element;

    if (!intentTarget || !isRefreshing) {
      return undefined;
    }

    const forgetOnIntent = (): void => {
      forgetScrollPosition({ area, pageKey });
    };

    SCROLL_POSITION_USER_INTENT_EVENTS.forEach(eventName =>
      intentTarget.addEventListener(eventName, forgetOnIntent, {
        passive: true,
      }),
    );

    return () => {
      SCROLL_POSITION_USER_INTENT_EVENTS.forEach(eventName =>
        intentTarget.removeEventListener(eventName, forgetOnIntent),
      );
    };
  }, [area, element, intentElement, isRefreshing, pageKey]);

  useLayoutEffect(() => {
    if (!element || isRefreshing) {
      return undefined;
    }

    const target = getRememberedScrollPosition({ area, pageKey });

    if (!target || (!target.scrollTop && !target.scrollLeft)) {
      return undefined;
    }

    const startedAt = Date.now();
    let frame: number | undefined;
    const intentTarget = intentElement ?? element;

    const stopRestoring = (): void => {
      isRestoringRef.current = false;

      if (frame !== undefined) {
        cancelAnimationFrame(frame);
        frame = undefined;
      }

      SCROLL_POSITION_USER_INTENT_EVENTS.forEach(eventName =>
        intentTarget.removeEventListener(eventName, stopRestoring),
      );
    };

    /* Hold the offset for the whole window rather than stopping the moment it first sticks. There is
    no single frame at which a refresh is done: the first write is clamped to whatever rows exist so
    far, a container measured by its own effect (the calendars) starts at zero height, and the pane
    is reset to the origin *after* this effect runs — by VirtualizedGrid's own mount effect and by
    react-window reacting to the new row data. A loop that stopped on first success handed the pane
    straight back to those resets. Only the user scrolling, or the deadline, ends it. */
    const applyTarget = (): void => {
      // Comparing first keeps the idle frames to two property reads: assigning a scroll offset the
      // element already holds still costs layout work, and this runs on a container whose content
      // can be hundreds of thousands of pixels tall.
      const isDrifted =
        element.scrollTop !== target.scrollTop ||
        element.scrollLeft !== target.scrollLeft;

      if (isDrifted) {
        element.scrollTop = target.scrollTop;
        element.scrollLeft = target.scrollLeft;
        onApplied?.();
      }

      if (Date.now() - startedAt > SCROLL_POSITION_RESTORE_TIMEOUT_MS) {
        stopRestoring();

        return;
      }

      frame = requestAnimationFrame(applyTarget);
    };

    isRestoringRef.current = true;

    SCROLL_POSITION_USER_INTENT_EVENTS.forEach(eventName =>
      intentTarget.addEventListener(eventName, stopRestoring, {
        passive: true,
      }),
    );

    applyTarget();

    return stopRestoring;
  }, [area, element, intentElement, isRefreshing, onApplied, pageKey]);
};
